import busyIndication from "@/utils/busyIndication";
import Transaction, { TransactionDefaultProps } from "../models/Transaction";
import TransactionApi from "./TransactionApi";
import TransactionItem, { TransactionItemDefaultProps } from "../models/TransactionItem";
import {BookType} from "../models/BookType";
import TransStat from "../models/TransStat";
import TransType from "../models/TransType";
import Currency from "../models/Currency";
import Dimension from "../models/Dimension";
import InvoiceMirror from "../models/InvoiceMirror";
import Car from "../models/Car";
import TransactionItemDimension, { TransactionItemDimensionDefaultProps } from "../models/TransactionItemDimension";
import BookAuditItem from "../models/BookAuditItem";

export class TransactionStore {
  transaction: Transaction = { ...TransactionDefaultProps }
  originalTransaction: Transaction = { ...TransactionDefaultProps }
  modifiedTransaction: any[] = [];

  //-----------Tranzakció tételek----------------//
  transactionItems: TransactionItem[] = [];
  originalTransactionItems: TransactionItem[] = [];

  // Igaz, amíg a tételek + dimenziók + törzsadatok töltenek — a tétel/dimenzió gridek erre
  // teszik ki a k-loading-mask rétegüket (TransactionItemEdit / TransactionItemDimensionEdit),
  // különben a betöltés alatt üresnek tűnne egy tételekkel rendelkező tranzakció.
  isItemsLoading = false;

  newItems: TransactionItem[] = [];
  modifiedItems: TransactionItem[] = [];
  deletedItems: number[] = [];

  selectedItem: TransactionItem = {...TransactionItemDefaultProps};
  positiveSelectedItem: TransactionItem = {...TransactionItemDefaultProps};
  negativeSelectedItem: TransactionItem = {...TransactionItemDefaultProps};
  selectedInvNoGroup = 1;

  //-----------Tranzakció dimenzió--------------------//
  transactionItemsDimensions: TransactionItemDimension[] = [];
  originalTransactionItemsDimensions: TransactionItemDimension[] = [];

  selectedDimension: TransactionItemDimension = {...TransactionItemDimensionDefaultProps};
  positiveSelectedDimension: TransactionItemDimension = {...TransactionItemDimensionDefaultProps};
  negativeSelectedDimension: TransactionItemDimension = {...TransactionItemDimensionDefaultProps};

  newDimensions: TransactionItemDimension [] = [];
  modifiedDimensions: TransactionItemDimension [] = [];
  deletedDimensions: number[] = [];
  
  positiveSelectedDimCodeType = 5;
  negativeSelectedDimCodeType = 5;

  isReadOnlyView = false;

  // --- Főkönyvi szám módosítás mód (K2D w_resp_trans ue_accno_mod) ---
  /** Bekapcsolva a tétel-rácsok főkönyvi szám cellái szerkeszthetők, minden más read-only marad. */
  isAccnoEditMode = false;
  /** tételId -> a választott ÚJ főkönyvi szám (invoiceMirror) id-ja. Csak accno-módban töltődik. */
  accnoChanges: Record<number, number> = {};
  /** Kézi tranzakciónál az "Eredeti tranzakció sztornózása" kapcsoló állása (gépinél a backend kényszeríti). */
  accnoModReverse = true;
  /** Az accno-módban felajánlható, SZŰKÍTETT főkönyvi szám lista (csak nyitott, kézi, nem fixált kontírszám). */
  accnoModMirrors: InvoiceMirror[] = [];

  //---------Tömbök---------------//
  stations: any[] = [];
  users: any[] = [];
  tools: any[] = [];
  accGroups: any[] = [];
  bookTypes: BookType[] = [];
  transStats: TransStat[] = [];
  transTypes: TransType[] = [];
  currencies: Currency[] = [];
  dimensions: Dimension[] = [];
  invoiceMirrors: InvoiceMirror[] = [];
  serCars: Car[] = [];
  projects: object[] = [];
  pos: object[] = [];
  periods: any[] = [];

  //---------- Tranzakciók ----------//

  // Nincs @busyIndication(): a hívók (TransactionEditor mounted/undoChanges/setStatus) a teljes
  // betöltési sorozatot (fej + tételek + dimenziók + törzsadatok) fogják egy overlay alá, és a
  // dekorátor finally-je a fej betöltése után idő előtt levenné az overlay-t — a tételek ilyenkor
  // még töltenek, és úgy tűnne, mintha a tranzakciónak nem lennének tételei.
  async loadTransaction(id: number) {
    const transaction = await TransactionApi.getTransaction(id);
    this.transaction = transaction;
    this.originalTransaction = JSON.parse(JSON.stringify(transaction));
  }

  async saveTransaction(id: number) {
    return await TransactionApi.save(id, this.transaction);
  }

  //---------- Tranzakció tételek ----------//

  async loadTransactionItems(id: number) {
    const items = await TransactionApi.getTransactionItems(id);
    // Összerendelés (pontozás) inicializálása a számlaszám (invNo) alapján: azonos invNo = egy
    // csoport, invNo = 1 = nincs párja. Egy tétel akkor pontozott, ha invNo != 1 ÉS van másik
    // tétel ugyanazzal az invNo-val (van párja). Így sablon-betöltés után a párok azonnal látszanak.
    const invNoCounts = items.reduce((acc: Record<number, number>, it: TransactionItem) => {
      acc[it.invNo] = (acc[it.invNo] || 0) + 1;
      return acc;
    }, {});
    items.forEach((it: TransactionItem) => {
      it.isHasDimensions = Number(it.invNo) !== 1 && invNoCounts[it.invNo] > 1;
    });
    this.transactionItems = items;
    this.originalTransactionItems = JSON.parse(JSON.stringify(items));
  }
  async saveTransactionItems(){
    return await TransactionApi.saveTransItems(this.newItems);
  }

  async deleteTransactionItems(){
    return await TransactionApi.deleteTransItems(this.deletedItems);
  }

  async updateTransactionItems(){
    return await TransactionApi.saveTransItems(this.modifiedItems);
  }

  //---------- Tranzakció tételek dimenziók ------//

  async loadTransactionDimensions(itemHeadList: number[]) {
    const dimensions = await TransactionApi.getTransactionDimensions(itemHeadList);
    this.transactionItemsDimensions = dimensions;
    this.originalTransactionItemsDimensions = JSON.parse(JSON.stringify(dimensions));
  }
  
  async saveTransactionDimensionItems(){
    return await TransactionApi.saveTransDimItems(this.newDimensions);
  }
  async deleteTransactionDimensionItems(){
    return await TransactionApi.deleteTransDimItems(this.deletedDimensions);
  }
  async updateTransactionDimensionItems(){
    return await TransactionApi.saveTransDimItems(this.modifiedDimensions);
  }

  //---------- Egyéb ----------//

  async loadAccGroup() {
    this.accGroups = await TransactionApi.getAccGroup();
  }
  async loadTools() {
    this.tools = await TransactionApi.getTools();
  }
  async loadUsers() {
    this.users = await TransactionApi.getUsers();
  }
  async loadStations() {
    this.stations = await TransactionApi.getStations();
  }
  async loadBookTypes() {
    this.bookTypes = await TransactionApi.getBookTypes();
  }

  async loadTransStats() {
    this.transStats = await TransactionApi.getTransStats();
  }

  async loadTransTypes() {
    this.transTypes = await TransactionApi.getTransTypes();
  }

  async loadCurrencies() {
    this.currencies = await TransactionApi.getCurrencies();
  }

  async loadPeriods(year: string) {
    this.periods = await TransactionApi.getPeriods(year);
  }
  async loadInvoiceMirrors(year: string){
    this.invoiceMirrors = await TransactionApi.getInvoiceMirrors(year);
  }
  async loadDimensions(year: string){
    this.dimensions = await TransactionApi.getDimensions(year);
  }
  async loadSerCars() {
      this.serCars = await TransactionApi.getCars();
      this.serCars = this.serCars.map(x => ({
        ...x,
        idString: String(x.id)
      }));
  }
  async loadProjectHeads() {
      this.projects = await TransactionApi.getProjects();
      this.projects = this.projects.map(x => ({
        ...x,
        idString: String((x as any).id)
      }))
  } 
  async loadPOs() {
      this.pos = await TransactionApi.getPOs();
      this.pos = this.pos.map(x => ({
        ...x,
        idString: String((x as any).id)
      }))
  }
  async isValidPartCode(partCode: string){
      return await TransactionApi.validPartCode(partCode);
  }
  async ValidPartnerName(partnerName: string){
      return await TransactionApi.validPartnerName(partnerName);
  }
  async getPeriod(date: string){
    return await TransactionApi.getPeriod(date)
  }  
  async loadUserSetting(setting: any){
    return await TransactionApi.loadUserSetting(setting)
  }
  async setUserSetting(setting: any){
    return await TransactionApi.setUserSetting(setting)
  } 
  async getInvoicePictures(invNo: number){
    return await TransactionApi.getInvoiceDocuments(invNo)
  }
  @busyIndication()
  async generateXML(item: BookAuditItem){
    return await TransactionApi.generateXML(item)
  }
  async openXMLFile(path: string){
    return await TransactionApi.openXMLFile(path)
  }
}

const store = new TransactionStore();
export default store;